import { useMemo } from "react";
import Typography from "core/components/Typography";
import NumberInput from "core/components/NumberInput";
import DatePicker from "core/components/Field/DatePicker";
import Toggle from "core/components/Toggle";
import { Label } from "core/components/Form";
import { Row } from "core/styles";
import Combobox from "core/components/Field/Combobox";
import Field from "core/components/Field";
import BulkPasteInput from "core/components/BulkPasteInput";
import QueryBuilder from "core/components/QueryBuilder";

const getControlComponent = (componentType) => {
  switch (componentType) {
    case "subtitle":
      return ({ text }) => (
        <Typography variant="h4" color="black" mb="xs">
          {text}
        </Typography>
      );

    case "description":
      return ({ text }) => (
        <Typography variant="p" mb="md">
          {text}
        </Typography>
      );

    case "number":
      return ({ id, value, setValue, error, dataTest, ...otherProps }) => (
        <NumberInput
          id={id}
          value={value}
          onChange={setValue}
          errorMessage={error}
          min="0"
          dataTest={dataTest}
          {...otherProps}
        />
      );

    case "date":
      return ({ id, value, setValue, error, ...otherProps }) => (
        <DatePicker
          id={id}
          value={value}
          onChange={setValue}
          errorMessage={error}
          {...otherProps}
        />
      );

    case "toggle":
      return ({ id, label, value, setValue, dataTest, ...otherProps }) =>
        label ? (
          <Row gap="xs" style={{ margin: "10px 0px" }}>
            <Toggle
              id={id}
              value={value}
              onChange={setValue}
              dataTest={dataTest}
              {...otherProps}
            />
            <Label>{label}</Label>
          </Row>
        ) : (
          <Toggle
            id={id}
            value={value}
            onChange={setValue}
            dataTest={dataTest}
            {...otherProps}
          />
        );

    case "combobox":
      return ({
        id,
        value,
        setValue,
        catalogFields,
        error,
        dataTest,
        toggleKey,
        // Belongs to the toggle row, not the Combobox.
        toggleLabel,
        ...otherProps
      }) => (
        <Combobox
          id={id}
          value={value}
          onChange={setValue}
          options={catalogFields}
          errorMessage={error}
          dataTest={dataTest ?? toggleKey}
          {...otherProps}
        />
      );

    case "mms":
      return ({
        id,
        value,
        setValue,
        catalogFields,
        error,
        dataTest,
        toggleKey,
        ...otherProps
      }) => (
        <Field
          type="mms"
          id={id}
          value={value}
          onChange={setValue}
          options={catalogFields}
          error={error}
          dataTest={dataTest ?? toggleKey}
          {...otherProps}
        />
      );

    case "bulkInput":
      return ({ id, value, setValue, validator, dataTest, ...otherProps }) => (
        <BulkPasteInput
          id={id}
          value={value || []}
          onChange={setValue}
          validator={validator}
          data-test={dataTest}
          {...otherProps}
        />
      );

    case "queryBuilder":
      return ({
        value,
        setValue,
        catalogFields,
        error,
        dataTest,
        ...otherProps
      }) => (
        <QueryBuilder
          filters={value}
          onChange={setValue}
          fields={catalogFields}
          errorMessage={error}
          dataTest={dataTest}
          {...otherProps}
        />
      );

    default:
      return null;
  }
};

const LicenseControl = ({ control, error, dataTest }) => {
  const { getValue, setValue, catalogFields, validator } = control.dynamicProps;

  const Component = useMemo(() => {
    return control.componentType === "custom"
      ? control.customComponent
      : getControlComponent(control.componentType);
  }, [control.componentType, control.customComponent]);

  const hasOptionalToggle =
    control.toggleKey && control.dynamicProps[control.toggleKey] === false;

  const value = getValue();
  const isToggleChecked = useMemo(() => {
    return hasOptionalToggle ? Array.isArray(value) : false;
  }, [value, hasOptionalToggle]);

  const handleToggleChange = (isChecked) => {
    if (isChecked) {
      setValue([]);
    } else {
      setValue(null);
    }
  };

  if (hasOptionalToggle) {
    return (
      <div
        style={{ display: "flex", alignItems: "start", gap: "15px" }}
        data-test={dataTest}
      >
        <div data-test={`${dataTest}-toggle`}>
          <Label style={{ marginBottom: "10px" }}>
            {control.staticProps.toggleLabel}
          </Label>
          <Toggle
            id={control.fieldKey}
            value={isToggleChecked}
            onChange={handleToggleChange}
            dataTest={dataTest}
          />
        </div>
        <div style={{ flex: 1 }} data-test={`${dataTest}-component`}>
          <Component
            id={control.fieldKey}
            value={getValue()}
            setValue={setValue}
            error={error}
            catalogFields={catalogFields}
            validator={validator}
            isDisabled={!isToggleChecked}
            dataTest={dataTest}
            toggleKey={control.toggleKey || ""}
            {...control.staticProps} // label, name, etc.
          />
        </div>
      </div>
    );
  }

  return (
    <Component
      id={control.fieldKey}
      value={getValue()}
      setValue={setValue}
      error={error}
      catalogFields={catalogFields}
      validator={validator}
      dataTest={control.fieldKey}
      {...{ ...control.dynamicProps, ...control.staticProps }} // label, name, etc.
    />
  );
};

export default LicenseControl;
